import React, { useMemo, useState } from 'react';
import { ChevronRightUniversal } from '@duxton-web/icons';
import { Table, Typography, type TableColumnsType } from '@duxton-web/ui';

import type { DeepDive, RawScorecardData } from '../../types';
import {
  calculateRelativeUplift,
  formatPValue,
  formatRelativeDelta,
} from '../../utils';

interface Props {
  deepDives?: DeepDive[] | null;
}

interface DeepDiveSectionProps {
  dimensionName: string;
  dives: DeepDive[];
}

interface DeepDiveTableRow {
  key: string;
  dimensionValue: string;
  treatmentId: number;
  warnings?: string[];
  analysisUnit: string;
  controlMean?: number;
  treatmentMean?: number;
  uplift: number | null;
  pValue: number | null;
  ciLower?: number;
  ciUpper?: number;
}

const DEEP_DIVE_PAGE_SIZE = 10;
const DEEP_DIVE_PAGE_SIZE_OPTIONS = ['10', '20', '50'];

const DeepDiveSection: React.FC<DeepDiveSectionProps> = ({
  dimensionName,
  dives,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const columns = useMemo<TableColumnsType<DeepDiveTableRow>>(
    () => [
      {
        title: dimensionName,
        dataIndex: 'dimensionValue',
        key: 'dimValue',
        width: 150,
      },
      {
        title: 'Treatment ID',
        dataIndex: 'treatmentId',
        key: 'treatmentId',
        width: 110,
        render: (id: number) => `Treatment ${id}`,
      },
      {
        title: 'Warnings',
        dataIndex: 'warnings',
        key: 'warnings',
        width: 110,
        render: (warnings: string[] | undefined) =>
          warnings?.length ? (
            <Typography.Text style={{ color: '#d48806', fontSize: 12 }}>
              {warnings[0]}
            </Typography.Text>
          ) : (
            '—'
          ),
      },
      {
        title: 'Analysis Unit',
        dataIndex: 'analysisUnit',
        key: 'analysisUnit',
        width: 120,
      },
      {
        title: 'Control (Mean)',
        dataIndex: 'controlMean',
        key: 'controlMean',
        width: 120,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
      {
        title: 'Treatment (Mean)',
        dataIndex: 'treatmentMean',
        key: 'treatmentMean',
        width: 130,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
      {
        title: 'p-value',
        dataIndex: 'pValue',
        key: 'pValue',
        width: 80,
        align: 'right',
        render: (value: number | null) => formatPValue(value),
      },
      {
        title: 'Uplift',
        key: 'uplift',
        width: 80,
        align: 'right',
        render: (_: unknown, record: DeepDiveTableRow) =>
          formatRelativeDelta(
            calculateRelativeUplift(record.controlMean, record.treatmentMean),
          ),
      },
      {
        title: 'CI (L)',
        dataIndex: 'ciLower',
        key: 'ciLower',
        width: 80,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
      {
        title: 'CI (U)',
        dataIndex: 'ciUpper',
        key: 'ciUpper',
        width: 80,
        align: 'right',
        render: (value: number | undefined) => value?.toFixed(4) ?? '—',
      },
    ],
    [dimensionName],
  );

  const dataSource = useMemo<DeepDiveTableRow[]>(
    () =>
      dives.map((dive, index) => {
        const raw = dive.scorecard.raw as RawScorecardData | null;
        const dataInfo = raw?.data_info;
        const controlMean =
          dataInfo?.treatment_0?.mean_w ?? dataInfo?.treatment_0?.mean;
        const treatmentData = dataInfo?.treatment_1;

        return {
          key: `${index}`,
          dimensionValue: dive.dimensionValue,
          treatmentId: dive.treatmentId,
          warnings: raw?.recommended_test?.warnings,
          analysisUnit: dataInfo?.id_name ?? '—',
          controlMean,
          treatmentMean: treatmentData?.mean_w ?? treatmentData?.mean,
          pValue: dive.scorecard.pValue ?? null,
          uplift: dive.scorecard.uplift ?? null,
          ciLower: dive.scorecard.confidenceInterval?.[0]?.value,
          ciUpper: dive.scorecard.confidenceInterval?.[1]?.value,
        };
      }),
    [dives],
  );

  return (
    <div
      style={{
        marginTop: 16,
        padding: '12px 16px 16px',
        background: '#f0f0f0',
        borderRadius: 6,
      }}
    >
      <div
        style={{
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          fontSize: 13,
          gap: 8,
        }}
        onClick={() => setIsOpen((value) => !value)}
      >
        <ChevronRightUniversal
          style={{
            fontSize: 10,
            transform: isOpen ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
        <Typography.Link>
          Dimension deep dive (by {dimensionName})
        </Typography.Link>
        <Typography.Text type="secondary" style={{ marginLeft: 4 }}>
          — scoped to filter
        </Typography.Text>
      </div>

      {isOpen && (
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={
            dataSource.length > DEEP_DIVE_PAGE_SIZE
              ? {
                  defaultPageSize: DEEP_DIVE_PAGE_SIZE,
                  pageSizeOptions: DEEP_DIVE_PAGE_SIZE_OPTIONS,
                  showSizeChanger: true,
                  showTotal: (total: number, range: [number, number]) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                  size: 'small',
                }
              : false
          }
          scroll={{ x: 'max-content' }}
          size="small"
          style={{ marginTop: 12 }}
        />
      )}
    </div>
  );
};

const DeepDives: React.FC<Props> = ({ deepDives }) => {
  const grouped = useMemo(
    () =>
      (deepDives ?? []).reduce<Record<string, DeepDive[]>>((acc, dive) => {
        (acc[dive.dimensionName] ??= []).push(dive);
        return acc;
      }, {}),
    [deepDives],
  );

  if (!deepDives?.length) return null;

  return (
    <>
      {Object.entries(grouped).map(([dimensionName, dives]) => (
        <DeepDiveSection
          key={dimensionName}
          dimensionName={dimensionName}
          dives={dives}
        />
      ))}
    </>
  );
};

export default DeepDives;
